import React, { useEffect, useRef, useCallback } from "react";

import { css } from "@compiled/react";

import ChevronDownIcon from "@atlaskit/icon/glyph/chevron-down";
import ChevronUpIcon from "@atlaskit/icon/glyph/chevron-up";

import {
    GlobalNavLinkContentProps,
    DropdownStateProps
} from "./nav-links-container";

export type GlobalNavLinkProps = GlobalNavLinkContentProps & DropdownStateProps;

const DropdownContentStyle = css({
    "display": "block",
    "opacity": 1,
    "insetBlockStart": "100%",
    "marginInlineStart": "-100%",
    "minWidth": "300px",
    "position": "absolute",
    "overflow": "hidden",
    "borderRadius": "15px",
    "border": "10px solid #fff",
    "boxShadow": "0 0 1px 0 #ccc, 0 5px 20px -5px rgba(0,0,0,0.1)",
    "order": 100,
    "backgroundColor": "#ffffff",
    "insetInlineStart": 0,
    "transition": "opacity 0.5s",
    "zIndex": 8,
    "animation": "fadein 0.25s 1",
    ".inner-dropdown": {
        display: "block"
    }
});

const GlobalNavLink = (props: GlobalNavLinkProps) => {
    const linkRef = useRef<HTMLDivElement | null>(null);

    const isCurrentOpenDropdown =
        props.currentOpenDropDown === props.globalLinksKey;

    const anchorLinkClassName = `nav-link-anchor ${
        !!isCurrentOpenDropdown ? "isActive" : ""
    }`;

    const linkClickAction: React.MouseEventHandler<HTMLAnchorElement> = (e) => {
        if (props.hasSubnav) {
            e.preventDefault();
            props.setCurrentOpenDropDown(props.globalLinksKey);
        }
    };

    const handleEscKeyEvent = useCallback(
        (event: KeyboardEvent) => {
            if (event.key === "Escape" && isCurrentOpenDropdown) {
                props.setCurrentOpenDropDown("");
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [isCurrentOpenDropdown]
    );

    const handleClickOutside = useCallback(
        (event: MouseEvent) => {
            if (
                linkRef.current &&
                !linkRef.current.contains(event.target as Node)
            ) {
                props.setCurrentOpenDropDown("");
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );

    useEffect(() => {
        // Attach the listeners on component mount.
        document.addEventListener("mousedown", handleClickOutside);
        document.addEventListener("keydown", handleEscKeyEvent, false);
        // Detach the listeners on component unmount.
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
            document.removeEventListener("keydown", handleEscKeyEvent, false);
        };
    }, [handleEscKeyEvent, handleClickOutside]);

    return (
        <>
            <a
                className={anchorLinkClassName}
                href={props.href ?? "#"}
                onClick={linkClickAction}
                onMouseEnter={() => {
                    if (!isCurrentOpenDropdown) {
                        props.setCurrentOpenDropDown(props.globalLinksKey);
                    }
                }}
                data-event="clicked"
                data-event-component="linkButton"
                data-event-container="headerDropdownLink"
                data-product-key={props.productKey ?? "jiraSoftware"}
            >
                {props.linkText}
                {!!props.hasSubnav && (
                    <div className="chevron-down-icon">
                        {isCurrentOpenDropdown ? (
                            <ChevronUpIcon label="" />
                        ) : (
                            <ChevronDownIcon label="" />
                        )}
                    </div>
                )}
            </a>
            {!!props.content && isCurrentOpenDropdown && (
                <div
                    css={DropdownContentStyle}
                    className="dropdown-content"
                    ref={linkRef}
                >
                    <div className="inner-dropdown">{props.content}</div>
                </div>
            )}
        </>
    );
};

export default GlobalNavLink;
